import { useEffect, useRef } from 'react';
import { Field } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';

import { icon_settings_configurations } from '@gn-audio/icons';
import { Button, JabraIcon, useFallbackTranslation } from '@gn-audio/jss-components';
import { PartnerOverview } from '@gn-audio/jss-core';

import { ExpertiseBadgeColors, ExpertiseOption } from './constants';
import PartnerOverviewBadge from './PartnerOverviewBadge';
import { PartnerOverviewLoader } from './PartnerOverviewLoader';

type PartnerLocatorResultsProps = {
  filteredPartners: PartnerOverview[];
  isLoading: boolean;
  noResultsMessage?: Field<string>;
  resultsTitle?: Field<string>;
  setShowFilters: (show: boolean) => void;
  showFilters: boolean;
  handleShowDetails: (partnerId: string) => void;
  showDetails: boolean;
  focusedPartnerId: string | null;
  badgeColorsMap: ExpertiseBadgeColors;
  selectedExpertise?: ExpertiseOption | null;
};

export const PartnerLocatorResults = ({
  filteredPartners,
  isLoading,
  noResultsMessage,
  resultsTitle,
  setShowFilters,
  showFilters,
  handleShowDetails,
  showDetails,
  focusedPartnerId,
  badgeColorsMap,
  selectedExpertise,
}: PartnerLocatorResultsProps) => {
  const { t } = useFallbackTranslation();
  const partnerRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  useEffect(() => {
    if (!showDetails && focusedPartnerId) {
      const buttonRef = partnerRefs.current[focusedPartnerId];

      if (buttonRef) {
        buttonRef.focus();
      }
    }
  }, [showDetails, focusedPartnerId]);

  return (
    <div className="w-full items-center rounded-md bg-blue-50 px-8 pb-8 pt-5">
      {filteredPartners.length === 0 && !isLoading ? (
        <div className="m-auto flex h-full items-center justify-center text-center text-gray-900">
          {noResultsMessage?.value}
        </div>
      ) : (
        <>
          <div className="mb-4 flex items-center justify-between font-bold">
            <div>
              {filteredPartners.length > 0 && (
                <>
                  {filteredPartners.length} {resultsTitle?.value}
                </>
              )}
            </div>
            <Button
              variant="secondary"
              className="flex md:hidden"
              size="s"
              iconPlacement="right"
              icon={<JabraIcon Icon={icon_settings_configurations} />}
              onClick={() => setShowFilters(!showFilters)}
            >
              {t('system.Filters')}
            </Button>
          </div>
          <div className="grid flex-1 grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-4">
            {isLoading && <PartnerOverviewLoader />}
            {!isLoading &&
              filteredPartners.map((partner) => {
                return (
                  <button
                    key={partner.id}
                    ref={(el) => {
                      partnerRefs.current[partner.id] = el;
                    }}
                    className="flex flex-col rounded-md border border-transparent bg-white p-0 pt-2 text-left transition hover:border-blue-300"
                    onClick={() => handleShowDetails(partner.id)}
                  >
                    <div className="flex h-8 items-center justify-end">
                      <PartnerOverviewBadge
                        partner={partner}
                        badgeColorsMap={badgeColorsMap}
                        selectedExpertise={selectedExpertise}
                      />
                    </div>
                    <div
                      className={classNames('flex-1 px-4 pt-3', {
                        'pb-4': !partner?.website,
                      })}
                    >
                      <p className="break-words font-bold text-black">{partner.name}</p>

                      <div className="my-4 flex-1 text-black">
                        <p>{partner.address1}</p>
                        <p>{partner.address2}</p>
                        <p>{partner.city}</p>
                        <p>{partner.zip}</p>
                      </div>
                    </div>
                    {partner?.website && (
                      <div className="px-4 pb-4">
                        <a
                          href={
                            partner?.website?.startsWith('http')
                              ? partner.website
                              : `https://${partner.website}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="break-all text-sm text-gray-700 hover:text-gray-900"
                        >
                          {partner.website}
                        </a>
                      </div>
                    )}
                  </button>
                );
              })}
          </div>
        </>
      )}
    </div>
  );
};
